import { motion } from "framer-motion";
import { HiArrowRight, HiCode, HiOutlineClipboardCheck } from "react-icons/hi";
import SectionTitle from "../components/SectionTitle";
import ServiceCard from "../components/ServiceCard";
import { serviceProcess, services, serviceTechnologies } from "../data/content";
import { fadeInUp, stagger } from "../utils/motion";

function ServicesSection() {
  return (
    <section id="services" className="relative py-24 sm:py-28">
      <div className="section-shell">
        <SectionTitle
          eyebrow="Services"
          title="Engineering Services Built For Scale"
          subtitle="From product ideation to deployment, BeeVerge delivers modern software systems with strategy, speed, quality checks, and long-term technical support."
        />

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 gap-6 md:grid-cols-2"
        >
          {services.map((service) => (
            <ServiceCard key={service.title} {...service} />
          ))}
        </motion.div>

        <div className="mt-16 grid gap-6 lg:grid-cols-[1fr_0.9fr]">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="glass rounded-2xl p-6 shadow-glass sm:p-8"
          >
            <div className="mb-6 flex items-center gap-3">
              <HiOutlineClipboardCheck className="h-7 w-7 text-cyan-300" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                  Delivery Process
                </p>
                <h3 className="mt-1 text-2xl font-bold text-white">How We Build Your Product</h3>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {serviceProcess.map((item) => (
                <article key={item.step} className="rounded-xl border border-white/10 bg-black/25 p-4">
                  <p className="text-sm font-bold text-cyan-300">{item.step}</p>
                  <h4 className="mt-2 text-lg font-semibold text-white">{item.title}</h4>
                  <p className="mt-2 text-sm leading-6 text-blue-100/75">{item.text}</p>
                </article>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="glass flex flex-col justify-between rounded-2xl p-6 shadow-glass sm:p-8"
          >
            <div>
              <div className="mb-6 flex items-center gap-3">
                <HiCode className="h-7 w-7 text-cyan-300" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                    Technology
                  </p>
                  <h3 className="mt-1 text-2xl font-bold text-white">Tools We Work With</h3>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {serviceTechnologies.map((tech) => (
                  <span
                    key={tech}
                    className="rounded-lg border border-cyan-200/15 bg-cyan-300/10 px-4 py-2 text-sm font-semibold text-cyan-100"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <p className="mt-6 text-sm leading-7 text-blue-100/78">
                We choose the stack around your product needs, budget, timeline, integrations, and
                future growth. The goal is simple: build software that is fast to launch and easy to
                maintain.
              </p>
            </div>

            <motion.a
              href="#contact"
              whileHover={{ scale: 1.03, x: 4 }}
              whileTap={{ scale: 0.98 }}
              className="mt-8 inline-flex w-fit items-center gap-3 rounded-lg border border-cyan-200/30 bg-cyan-300/10 px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-cyan-100 transition hover:shadow-neon"
            >
              Start A Project
              <HiArrowRight className="h-5 w-5" />
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default ServicesSection;
